import {
  Banner,
  BlockStack,
  Button,
  Checkbox,
  DataTable,
  InlineStack,
  LegacyCard,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMemo, useState } from "react";


const normalizeRoutes = items => items.map(item => ({ ...item, _key: String(item.id || item.channel_code), weight: Number(item.weight), status: Boolean(Number(item.status)) }));

const defaultRoutes = () => [
  { _key: "stripe", channel_code: "stripe", weight: 100, status: true },
  { _key: "airwallex", channel_code: "airwallex", weight: 0, status: true },
];

const normalizeChannelCode = (value) =>
  value.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");

export default function CardPaymentRoute({ isTest, initialRoutes, request, onRoutesChange }) {
  const [routes, setRoutes] = useState(() => normalizeRoutes(initialRoutes || []));
  const [newChannel, setNewChannel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const enabledTotal = useMemo(
    () =>
      routes
        .filter((route) => route.status)
        .reduce((total, route) => total + Number(route.weight || 0), 0),
    [routes],
  );

  const updateRoute = (key, field, value) => {
    setSuccess("");
    setRoutes((previous) =>
      previous.map((route) =>
        route._key === key ? { ...route, [field]: value } : route,
      ),
    );
  };

  const addChannel = () => {
    const channelCode = normalizeChannelCode(newChannel);
    if (['paypal', 'cod'].includes(channelCode)) {
      setError('PayPal and COD do not participate in card routing.');
      return;
    }
    if (!channelCode || !/^[a-z][a-z0-9_]*$/.test(channelCode)) {
      setError("Channel code must start with a letter and use lowercase letters, numbers, or underscores.");
      return;
    }
    if (routes.some((route) => route.channel_code === channelCode)) {
      setError("This channel already exists.");
      return;
    }
    setRoutes((previous) => [
      ...previous,
      {
        _key: `${channelCode}-${Date.now()}`,
        channel_code: channelCode,
        weight: 0,
        status: true,
      },
    ]);
    setNewChannel("");
    setError("");
    setSuccess("");
  };

  const removeChannel = (key) => {
    setRoutes((previous) => previous.filter((route) => route._key !== key));
    setSuccess("");
  };

  const saveRoutes = async () => {
    if (enabledTotal !== 100) {
      setError("Enabled channel weights must total 100% before saving.");
      return;
    }
    if (!routes.length) {
      setError("At least one payment channel is required.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const savedRoutes = await request('u-card-payment-route', {
        is_test: isTest ? 1 : 0,
        routes: routes.map((route) => ({
          channel_code: route.channel_code,
          weight: Number(route.weight || 0),
          status: route.status ? 1 : 0,
        })),
      });
      setRoutes(normalizeRoutes(savedRoutes || []));
      onRoutesChange?.(savedRoutes || []);
      setSuccess("Card payment routing weights saved.");
    } catch (requestError) {
      const validationMessage = requestError.response?.data?.errors
        ? Object.values(requestError.response.data.errors).flat()[0]
        : "";
      setError(
        validationMessage ||
          requestError.response?.data?.message ||
          requestError.message ||
          "Unable to save card payment routes.",
      );
    } finally {
      setLoading(false);
    }
  };

  const useRandomRouting = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const savedRoutes = await request('u-card-payment-route', {
        is_test: isTest ? 1 : 0,
        routes: [],
      });
      setRoutes(normalizeRoutes(savedRoutes || []));
      onRoutesChange?.(savedRoutes || []);
      setSuccess("Random routing mode enabled.");
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.message ||
          "Unable to enable random routing.",
      );
    } finally {
      setLoading(false);
    }
  };

  const rows = routes.map((route) => [
    <TextField
      key={`channel-${route._key}`}
      value={route.channel_code}
      onChange={(value) =>
        updateRoute(route._key, "channel_code", normalizeChannelCode(value))
      }
      label="Channel code"
      labelHidden
      autoComplete="off"
    />,
    <Checkbox
      key={`status-${route._key}`}
      label="Enabled"
      labelHidden
      checked={route.status}
      onChange={(value) => updateRoute(route._key, "status", value)}
    />,
    <TextField
      key={`weight-${route._key}`}
      type="number"
      value={`${route.weight}`}
      min={0}
      max={100}
      suffix="%"
      onChange={(value) =>
        updateRoute(
          route._key,
          "weight",
          Math.min(100, Math.max(0, Number(value || 0))),
        )
      }
      label="Weight"
      labelHidden
      autoComplete="off"
    />,
    <Button
      key={`remove-${route._key}`}
      variant="plain"
      tone="critical"
      onClick={() => removeChannel(route._key)}
    >
      Remove
    </Button>,
  ]);

  return (
    <LegacyCard sectioned>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Card payment routing</Text>
            <Text as="p" tone="subdued">Routing weights for {isTest ? 'Test' : 'Live'}. PayPal and COD are not included.</Text>
            {error ? (
              <Banner tone="critical" onDismiss={() => setError("")}>
                <p>{error}</p>
              </Banner>
            ) : null}
            {success ? (
              <Banner tone="success" onDismiss={() => setSuccess("")}>
                <p>{success}</p>
              </Banner>
            ) : null}
            {!routes.length ? (
              <Banner tone="info">
                <p>
                  No routing weights are configured. Credit-card accounts use
                  the existing random selection rule.
                </p>
              </Banner>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "numeric", "text"]}
                headings={["Channel", "Enabled", "Weight", "Action"]}
                rows={rows}
              />
            )}
            {!routes.length ? (
              <InlineStack align="end">
                <Button onClick={() => setRoutes(defaultRoutes())}>
                  Configure weighted routing
                </Button>
              </InlineStack>
            ) : null}
            {routes.length ? (
              <>
                <InlineStack gap="200" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Add payment channel"
                      placeholder="e.g. adyen"
                      value={newChannel}
                      onChange={setNewChannel}
                      autoComplete="off"
                    />
                  </div>
                  <Button disabled={!newChannel.trim()} onClick={addChannel}>
                    Add channel
                  </Button>
                </InlineStack>
                <InlineStack align="space-between" blockAlign="center">
                  <Text
                    as="p"
                    tone={enabledTotal === 100 ? "success" : "critical"}
                    fontWeight="semibold"
                  >
                    Enabled total: {enabledTotal}%
                  </Text>
                  <InlineStack gap="200">
                <Button
                  variant="secondary"
                  loading={loading}
                  onClick={useRandomRouting}
                >
                  Use random routing
                </Button>
                  <Button
                    variant="primary"
                    loading={loading}
                    disabled={enabledTotal !== 100}
                    onClick={saveRoutes}
                  >
                    Save routing
                  </Button>
                  </InlineStack>
                </InlineStack>
              </>
            ) : null}
          </BlockStack>
    </LegacyCard>
  );
}
